import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "warning";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-label-on-accent hover:bg-accent-hover active:bg-accent-active shadow-sm focus-visible:ring-accent-soft",
  secondary:
    "bg-card text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary focus-visible:ring-separator-opaque",
  danger:
    "bg-status-red text-label-on-accent hover:opacity-90 active:opacity-80 shadow-sm focus-visible:ring-status-red",
  warning:
    "bg-status-orange text-label-on-accent hover:opacity-90 active:opacity-80 shadow-sm focus-visible:ring-status-orange",
  ghost:
    "bg-transparent text-label hover:bg-fill-tertiary focus-visible:ring-separator-opaque",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "primary", size = "md", loading = false, leftIcon, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-grouped",
        "disabled:cursor-not-allowed disabled:opacity-60",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {!loading && leftIcon}
      <span>{children}</span>
    </button>
  );
});
